import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/hooks/useStore";
import { useAuth } from "@/hooks/useAuth";
import { PageTitle, Panel } from "@/components/ui/Panel";
import { Input, Select } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import type { Card } from "@/types/card";

const TYPE_OPTIONS = [
  "All",
  "Champion",
  "Armor",
  "Equipment",
  "Signature Move",
  "Stance",
  "Utility",
  "Attack/Defend",
  "Token",
];

const STATUS_OPTIONS = ["All", "Draft", "Testing", "Finalized"];

export function AllCards() {
  const { state } = useStore();
  const { profile } = useAuth();
  const isAdmin = profile?.role === "admin";

  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [status, setStatus] = useState("All");
  const [setName, setSetName] = useState("All");

  const sets = useMemo(
    () => ["All", ...Array.from(new Set(state.cards.map((c) => c.set))).filter(Boolean)],
    [state.cards],
  );

  const cards = useMemo(() => {
    let list = state.cards.slice();
    if (q.trim()) {
      const t = q.toLowerCase().trim();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(t) ||
          c.setCardNumber?.toLowerCase().includes(t) ||
          c.rulesText?.toLowerCase().includes(t),
      );
    }
    if (type !== "All") list = list.filter((c) => c.cardType === type);
    if (status !== "All") list = list.filter((c) => c.status === status);
    if (setName !== "All") list = list.filter((c) => c.set === setName);
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [state.cards, q, type, status, setName]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <PageTitle>All Cards <span className="text-[#64748b] text-base font-normal">({cards.length})</span></PageTitle>
        {isAdmin && (
          <Link to="/cards/add">
            <Button>+ Add Card</Button>
          </Link>
        )}
      </div>

      <Panel className="grid gap-3 sm:grid-cols-4">
        <div>
          <label className="gm-label">Search</label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, number, text…" />
        </div>
        <div>
          <label className="gm-label">Type</label>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </Select>
        </div>
        <div>
          <label className="gm-label">Status</label>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((o) => <option key={o}>{o}</option>)}
          </Select>
        </div>
        <div>
          <label className="gm-label">Set</label>
          <Select value={setName} onChange={(e) => setSetName(e.target.value)}>
            {sets.map((o) => <option key={o}>{o}</option>)}
          </Select>
        </div>
      </Panel>

      {cards.length === 0 ? (
        <Panel><p className="text-sm text-[#64748b]">No cards match your filters.</p></Panel>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {cards.map((c) => <CardTile key={c.id} card={c} />)}
        </div>
      )}
    </div>
  );
}

function CardTile({ card }: { card: Card }) {
  return (
    <Link to={`/cards/${card.id}`} className="group">
      <div className="gm-panel overflow-hidden transition-colors hover:border-amber/50">
        <div className="aspect-[3/4] bg-[#0f1115]">
          {card.imageDataUrl ? (
            <img src={card.imageDataUrl} alt={card.name} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[#64748b] text-xs">no image</div>
          )}
        </div>
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-semibold">{card.name}</p>
              <p className="text-[11px] text-[#64748b]">{card.cardType} · {card.rarity}</p>
            </div>
            <StatusBadge status={card.status} />
          </div>
          {card.setCardNumber && (
            <p className="mt-1 font-mono text-[10px] text-[#64748b]">{card.setCardNumber}</p>
          )}
        </div>
      </div>
    </Link>
  );
}

function StatusBadge({ status }: { status: Card["status"] }) {
  const tone = status === "Finalized" ? "green" : status === "Testing" ? "amber" : "default";
  return <Badge tone={tone}>{status}</Badge>;
}
